import type { FC, ReactNode } from 'react'
import type { SiteSettings } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { navLinks } from '@/constants/site'

import SiteLogo from '@/components/landing/site-logo'

interface SiteHeaderProps {
    settings: SiteSettings | null
}

const SiteHeader: FC<SiteHeaderProps> = ({ settings }): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 12)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    const close = () => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-colors duration-500',
                scrolled || open ? 'border-b border-border bg-background/90 backdrop-blur-md' : 'border-b border-transparent bg-transparent'
            )}
        >
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between px-5 sm:px-8'>
                <Link
                    href='/'
                    onClick={close}
                    aria-label='Spalogy home'
                    className='rounded-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    <SiteLogo settings={settings} />
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-9 lg:flex'>
                    {navLinks.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className='eyebrow text-foreground/70 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:underline focus-visible:underline-offset-8'
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-3'>
                    <Link
                        href='/#contact'
                        className='hidden items-center gap-2 bg-primary px-5 py-3 text-primary-foreground transition-colors hover:bg-ocean focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:inline-flex'
                    >
                        <span className='eyebrow'>Get in touch</span>
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-11 items-center justify-center text-foreground focus-visible:outline-2 focus-visible:outline-ring lg:hidden'
                    >
                        {open ? <X className='size-6' aria-hidden='true' /> : <Menu className='size-6' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                hidden={!open}
                className='h-[calc(100svh-4.5rem)] overflow-y-auto bg-background px-5 pb-10 pt-6 sm:px-8 lg:hidden'
            >
                <nav aria-label='Mobile' className='flex flex-col'>
                    {navLinks.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            onClick={close}
                            className='display border-b border-border py-5 text-2xl text-foreground focus-visible:outline-none focus-visible:text-ocean'
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <Link
                    href='/#contact'
                    onClick={close}
                    className='mt-8 inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-4 text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                >
                    <span className='eyebrow'>Get in touch</span>
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </Link>
            </div>
        </header>
    )
}

export default SiteHeader
